import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface LoadingErrorStateProps {
  isLoading?: boolean
  error?: unknown
  loadingMessage?: string
  errorMessage?: string
  children?: ReactNode
}

export function LoadingErrorState({
  isLoading,
  error,
  loadingMessage,
  errorMessage,
  children,
}: LoadingErrorStateProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin mb-4" />
        <p className="text-sm text-neutral-500">{loadingMessage ?? t('loading.default')}</p>
      </div>
    )
  }

  if (error) {
    const fallback =
      errorMessage || (error instanceof Error ? error.message : t('errors.loadData'))

    const fullErrorMsg = (() => {
      if (typeof error === 'object' && error !== null) {
        const anyErr = error as any
        if (typeof anyErr.userMessage === 'string' && anyErr.userMessage.trim().length > 0) {
          return anyErr.userMessage
        }
        const serverMessage = anyErr.response?.data?.message
        if (typeof serverMessage === 'string' && serverMessage.trim().length > 0) {
          return serverMessage
        }
      }
      return fallback
    })()

    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 rounded-full bg-error-50 flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-error-600 mb-1">{t('errorPage.errorLabel')}</p>
        <p className="text-sm text-neutral-500 text-center max-w-md">{fullErrorMsg}</p>
      </div>
    )
  }

  return <>{children}</>
}
